import { memo, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { TechniqueStatus } from '../types';
import type { TechniqueType } from '../types';
import { TYPE_ICONS } from '../data/icons';
import { Pencil, Star, Info, Archive, ArchiveRestore, Play, Eye, Check, X, Trash2, Share2, Copy, Layers, ExternalLink, TriangleAlert } from 'lucide-react';
import { isMobile } from './PipPlayer';
import { shareTechnique } from '../utils/share';
import RuleInfoModal from './RuleInfoModal';
import type { Rule } from '../utils/legality';

const STATUS_LABELS: Record<TechniqueStatus, string> = {
  [TechniqueStatus.Unchecked]: 'Not started',
  [TechniqueStatus.MarkedForPractice]: 'Marked for practice',
  [TechniqueStatus.Completed]: 'Completed',
  [TechniqueStatus.Archived]: 'Archived',
};

interface TechniqueCardProps {
  id: string;
  name: string;
  japaneseName?: string;
  /** Set when a search found this row by another of its names: shown in
   *  place of the credit, so the hit makes sense. */
  matchedAlias?: string;
  /** This row's version picker is open: lift it above the picker's backdrop
   *  so the move it's choosing between stays readable. */
  picking?: boolean;
  type?: TechniqueType;
  status: TechniqueStatus;
  categoryColor: string;
  instagramUrl?: string;
  author?: string;
  attribution?: string;
  /** Sport rules note: the condition, or "Banned", for the arts in view — the icon's tooltip. */
  legality?: string;
  /** The rule behind it, for the modal the icon opens. */
  rule?: Rule;
  watched?: boolean;
  /** How many clips this card stands in for. >1 collapses the card into a
   *  group: it opens the version chooser instead of playing, and drops the
   *  per-clip buttons (they live on the chooser's rows instead). */
  variantCount?: number;
  onVariantsClick?: () => void;
  /** Desktop-only: open every version in the group as a grid of PiP players —
   *  the same move the category header's "open all" makes. */
  onOpenAllVersions?: () => void;
  onVideoClick?: () => void;
  onPipClick?: (url: string, name: string) => void;
  /** Toggle this technique on/off the user's mat (practice queue). */
  onToggleMat?: () => void;
  isAdmin?: boolean;
  onEditClick?: () => void;
  /** Open the technique details modal. */
  onInfoClick?: () => void;
  /** Archive this technique (Favorites view) — moves it to the Archived list. */
  onArchive?: () => void;
  /** Un-archive this technique (Archived view) — returns it to Favorites. */
  onUnarchive?: () => void;
  /** Admin review state — tints the card and enables approve/reject or restore/delete actions. */
  reviewState?: 'pending' | 'archived';
  onApprove?: () => void;
  onReject?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
}

function TechniqueCard({ id, name, type, status, categoryColor, instagramUrl, author, attribution, legality, rule, watched, matchedAlias, picking, variantCount, onVariantsClick, onOpenAllVersions, onVideoClick, onPipClick, onToggleMat, isAdmin, onEditClick, onInfoClick, onArchive, onUnarchive, reviewState, onApprove, onReject, onRestore, onDelete }: TechniqueCardProps) {
  const [ruleOpen, setRuleOpen] = useState(false);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  const TypeIcon = type ? TYPE_ICONS[type] : undefined;
  // On mobile, only the play button opens the video — a tap anywhere on the card
  // is too easy to trigger by accident. On desktop the whole card stays clickable.
  const mobile = isMobile();
  // A grouped card is a stand-in for several clips, so every per-clip
  // affordance on it (play, star, share, details) gives way to one action:
  // open the chooser and pick a version.
  const grouped = (variantCount ?? 1) > 1 && !!onVariantsClick;

  const openVideo = () => {
    if (!instagramUrl) return;
    if (onVideoClick) onVideoClick();
    if (onPipClick) onPipClick(instagramUrl, name);
  };

  return (
    <div
      ref={setNodeRef}
      data-id={id}
      className={`technique-card${picking ? ' technique-card--picking' : ''}${watched ? ' technique-card--watched' : ''}${reviewState === 'pending' ? ' technique-card--pending' : ''}${reviewState === 'archived' ? ' technique-card--archived-admin' : ''}`}
      style={isDragging ? { background: '#353560', borderColor: 'rgba(74, 158, 255, 0.4)' } : undefined}
      title={grouped ? `${variantCount} versions` : (instagramUrl ? 'Watch video' : STATUS_LABELS[status])}
      // Desktop: click anywhere on the card opens the PiP (a >8px drag instead
      // spawns it wherever dropped, handled in AppShell). Mobile: only the play
      // button opens it — see below — so a stray tap doesn't fire the video.
      onClick={grouped ? onVariantsClick : (instagramUrl && !mobile ? openVideo : undefined)}
      {...attributes}
      {...listeners}
    >
      <span className="technique-tier-bar" style={{ backgroundColor: categoryColor }} />

      {/* Leading play zone. On desktop it's a visual affordance for the card's
          click-to-play; on mobile it's the actual (and only) play trigger. */}
      {grouped ? (
        <button
          type="button"
          className="technique-play technique-play--group"
          onClick={(e) => { e.stopPropagation(); onVariantsClick!(); }}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={`Choose from ${variantCount} versions`}
        >
          <Layers size={15} />
        </button>
      ) : instagramUrl && (
        <button
          type="button"
          className={`technique-play${watched ? ' technique-play--watched' : ''}`}
          onClick={(e) => { e.stopPropagation(); openVideo(); }}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={watched ? 'Replay video' : 'Play video'}
        >
          {watched ? <Eye size={15} /> : <Play size={15} fill="currentColor" />}
        </button>
      )}

      {TypeIcon && <TypeIcon size={16} className="technique-type-icon" />}

      <span className="technique-label">
        <span className="technique-name-row">
          {name}
          {legality && rule && !grouped && (
            <button
              type="button"
              className="technique-rule-icon"
              title={legality}
              aria-label={`Sport rules: ${legality}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setRuleOpen(true); }}
            >
              <TriangleAlert size={14} />
            </button>
          )}
        </span>
        {/* Credit sub-text: the technique's attribution wins; the poster's
            @handle is the fallback when no attribution is set. */}
        {grouped ? (
          <span className="technique-author technique-author--group">{variantCount} versions</span>
        ) : matchedAlias ? (
          <span className="technique-author technique-author--match">matches {matchedAlias}</span>
        ) : (attribution || author) && (
          // Poster first (the handle opens their page), then the athlete in
          // the clip when that's someone else.
          <span className="technique-author">
            {author ? `@${author}` : ''}
            {author && attribution ? ' · ' : ''}
            {attribution ?? ''}
          </span>
        )}
      </span>

      {/* Desktop only, and by the same test the handler uses — the width media
          query that used to hide this left it on tablets and on a phone asking
          for the desktop site, where openPipGroup does nothing. */}
      {grouped && onOpenAllVersions && !mobile && (
        <div className="video-buttons" onPointerDown={(e) => e.stopPropagation()}>
          <button
            className="video-link video-link--playall"
            onClick={(e) => { e.stopPropagation(); onOpenAllVersions(); }}
            aria-label={`Open all ${variantCount} versions in picture-in-picture`}
            title="Open all videos"
          >
            <ExternalLink size={12} />
          </button>
        </div>
      )}

      {!grouped && (
      <div className="video-buttons" onPointerDown={(e) => e.stopPropagation()}>
        {onToggleMat && (
          <button
            className={`video-link video-link--star${status === TechniqueStatus.MarkedForPractice ? ' video-link--starred' : ''}`}
            onClick={(e) => { e.stopPropagation(); onToggleMat(); }}
            aria-label={status === TechniqueStatus.MarkedForPractice ? 'Remove from Favorites' : 'Add to Favorites'}
            title={status === TechniqueStatus.MarkedForPractice ? 'In favorites: tap to remove' : 'Add to Favorites'}
          >
            <Star size={12} fill={status === TechniqueStatus.MarkedForPractice ? 'currentColor' : 'none'} />
          </button>
        )}
        {isAdmin && onEditClick && (
          <button
            className="video-link video-link--edit"
            onClick={(e) => { e.stopPropagation(); onEditClick(); }}
            aria-label="Edit technique"
            title="Edit technique"
          >
            <Pencil size={10} />
          </button>
        )}
        <button
          className="video-link video-link--share"
          onClick={(e) => { e.stopPropagation(); shareTechnique(id, name, mobile); }}
          aria-label={mobile ? 'Share technique' : 'Copy link to technique'}
          title={mobile ? 'Share this technique' : 'Copy link to this technique'}
        >
          {mobile ? <Share2 size={12} /> : <Copy size={12} />}
        </button>
        {onInfoClick && (
          <button
            className="video-link video-link--info"
            onClick={(e) => { e.stopPropagation(); onInfoClick(); }}
            aria-label="Technique details"
            title="Technique details"
          >
            <Info size={12} />
          </button>
        )}
        {onArchive && (
          <button
            className="video-link video-link--archive"
            onClick={(e) => { e.stopPropagation(); onArchive(); }}
            aria-label="Archive technique"
            title="Archive: move to Archived"
          >
            <Archive size={12} />
          </button>
        )}
        {onUnarchive && (
          <button
            className="video-link video-link--archive"
            onClick={(e) => { e.stopPropagation(); onUnarchive(); }}
            aria-label="Un-archive technique"
            title="Un-archive: return to Favorites"
          >
            <ArchiveRestore size={12} />
          </button>
        )}
        {reviewState === 'pending' && onReject && (
          <button
            className="video-link video-link--reject"
            onClick={(e) => { e.stopPropagation(); onReject(); }}
            aria-label="Reject submission"
            title="Reject: send to archive"
          >
            <X size={12} />
          </button>
        )}
        {reviewState === 'pending' && onApprove && (
          <button
            className="video-link video-link--approve"
            onClick={(e) => { e.stopPropagation(); onApprove(); }}
            aria-label="Approve submission"
            title="Approve: add to library"
          >
            <Check size={12} />
          </button>
        )}
        {reviewState === 'archived' && onRestore && (
          <button
            className="video-link video-link--approve"
            onClick={(e) => { e.stopPropagation(); onRestore(); }}
            aria-label="Restore from archive"
            title="Restore: return to library"
          >
            <ArchiveRestore size={12} />
          </button>
        )}
        {reviewState === 'archived' && onDelete && (
          <button
            className="video-link video-link--reject"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            aria-label="Delete permanently"
            title="Delete permanently"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      )}
      {ruleOpen && rule && <RuleInfoModal rule={rule} onClose={() => setRuleOpen(false)} />}
    </div>
  );
}

export default memo(TechniqueCard);
